import { Trans, t } from "@lingui/macro";
import { PlusCircle, MinusCircle, Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useState } from "react";
import { GetOfficeNotePagesResponse, ONPageItem } from "../../models/DTO";
import { Soribot } from "../../models/Soribot";
import { SourceOfficeNotePage } from "../../models/SoribotSource";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { OfficeNoteIcon } from "./compontents/Icons";
import { getSourceInfo } from "./SoribotDatasources";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { IconPlus } from '../components/FigmaIcons';

export const SoribotDatasourcesOfficeNote: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [officeNoteKey, setOfficeNoteKey] = useState<string | null>();
  const [pagesResp, setPagesResp] = useState<GetOfficeNotePagesResponse>();
  const [inProgress, setInProgress] = useState(false);
  const [inProgressAddPageId, setInProgressAddPageId] = useState('');
  const [inProgressUpdate, setInProgressUpdate] = useState(false);
  const { toast } = useToast();
  const [sourcePages, setSourcePages] = useState<SourceOfficeNotePage[]>([]);
  const [removedSourcePages, setRemovedSourcePages] = useState<SourceOfficeNotePage[]>([]);
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });

  useEffect(() => {
    setSourcePages([])
    reloadSourcePages();
  }, [bot.id]);

  return <>
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue} className="w-full bg-background rounded border mb-4">
      <AccordionItem value="add-officenote" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline text-sm font-semibold">
          <div className="flex items-center"><IconPlus className="w-4 h-4 mr-2" /> <Trans>Add pages from your OfficeNote space</Trans></div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="p-4 border rounded bg-muted/50 flex items-end gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="otp"><Trans>One-time-password (OTP)</Trans></Label>
              <Input id="otp" value={officeNoteKey || ""} placeholder={t`Enter a value`} onChange={e => setOfficeNoteKey(e.target.value)} />
            </div>
            <Button disabled={!officeNoteKey} onClick={fetchPages}><Trans>Fetch</Trans></Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>

    {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>}
    {!inProgress && <>
      {pagesResp && <>
        <div className="flex justify-between items-center px-2 mt-4 text-xs font-bold text-muted-foreground uppercase">
          <span><Trans>Found Pages</Trans></span>
          <span><Trans>Click to add</Trans></span>
        </div>
        <div className="border rounded bg-background mt-1 divide-y">
          {pagesResp.error && <div className="p-3 text-destructive text-sm whitespace-pre-wrap">{pagesResp.error}</div>}
          {pagesResp.pages && <div className="p-2">{showPageItems(pagesResp.pages, 0)}</div>}
        </div>
      </>}

      <h4 className="text-sm font-bold text-muted-foreground px-2 mt-6 uppercase"><Trans>Added pages</Trans></h4>
      {sourcePages.length === 0 && <div className="p-4 text-center text-muted-foreground"><Trans>No pages added</Trans></div>}
      <div className="space-y-2 mt-1">
        {sourcePages.map(p => sourcePageItem(p))}
      </div>
      <br />
      <div className="px-2">
        <Button disabled={inProgressUpdate} onClick={updateBotContent}><RefreshCw className={`w-4 h-4 mr-2 ${inProgressUpdate ? 'animate-spin' : ''}`} /><Trans>Sync bot knowledge</Trans></Button>
      </div>
    </>}

    <div className="mt-8 p-4 border rounded bg-muted/50 text-sm text-card-foreground space-y-2">
      <div className="flex items-center gap-2">
        <a href="https://officenote.co.kr" target="_blank" rel="noreferrer">
          <img src="/assets/icon/officenote-logo.png" style={{ height: 24 }} alt="OfficeNote Logo" />
        </a>
      </div>
      <p>OfficeNote is a cloud-based document management and collaboration tool that is designed to improve work productivity by reducing the time and effort spent on document creation, information search, and communication. It is based on the organizational structure of each company and allows users to create, share, and manage documents easily and efficiently. It has a simple and intuitive UI/UX that is designed for professional work culture. It also supports various document formats, templates, and history tracking features. OfficeNote is developed by Jiransoft, a software company that has 24 years of experience and expertise in web editing solutions.</p>
    </div>

    <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alert.title}</AlertDialogTitle>
          <AlertDialogDescription>{alert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setAlert(prev => ({ ...prev, open: false }))}><Trans>OK</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>

  function showPageItems(pages: ONPageItem[], level: number): any {
    return pages.map(p => <div key={p.uuid} style={{ paddingLeft: level * 20 }} className="py-1">
      <div className={`flex items-center justify-between p-2 rounded ${isAlreadyAdded(p.uuid) ? 'opacity-50 pointer-events-none' : 'hover:bg-muted'}`}>
        <div className="flex items-center gap-2 truncate text-sm">
          <OfficeNoteIcon /> {p.title}
        </div>
        {inProgressAddPageId === p.uuid ?
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          :
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => addPageAsSourcePage(p)}>
            <PlusCircle className="w-4 h-4 text-blue-500" />
          </Button>
        }
      </div>
      {p.children && showPageItems(p.children, level + 1)}
    </div>)
  }

  function sourcePageItem(p: SourceOfficeNotePage) {
    const toRemove = removedSourcePages.some(b => b.id === p.id);
    return <div key={p.id} className={`flex items-center justify-between p-3 rounded border bg-background ${toRemove ? 'opacity-50' : 'hover:bg-muted/50'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-medium truncate text-sm">
          <OfficeNoteIcon />{p.title}
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate pl-5">
          {getSourceInfo(toRemove, p)}
        </div>
      </div>
      {inProgressUpdate ?
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground shrink-0" />
        :
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0" onClick={() => removeSourcePage(p)}>
          <MinusCircle className="w-5 h-5" />
        </Button>
      }
    </div>;
  }

  async function reloadSourcePages() {
    setInProgress(true);
    const pages = await Fire.bot.getSourceOfficeNotePages(bot.id)
    setSourcePages(pages);
    setInProgress(false);
  }

  async function fetchPages() {
    if (officeNoteKey) {
      setInProgress(true);
      try {
        const data = await Fire.bot.getPagesFromOfficeNote({ soribotId: bot.id, officeNoteKey: officeNoteKey })
        setOfficeNoteKey(null);
        setPagesResp(data);
      } catch (error: any) {
        if (error.message.includes('401')) {
          setAlert({ open: true, title: t`Error 401`, message: t`Your OTP may be invalid. Please create another one.`, confirm: null });
        } else {
          setAlert({ open: true, title: t`Error`, message: error.message, confirm: null });
        }
      } finally {
        setInProgress(false);
      }
    }
  }

  async function addPageAsSourcePage(page: ONPageItem) {
    setInProgressAddPageId(page.uuid);
    const sp = await Fire.bot.saveSourceOfficeNotePage(My.user.id, bot.id, page);
    setInProgressAddPageId('');
    toast({ title: t`Added page ${page.title}` });
    setSourcePages([...sourcePages, sp]);
  }

  async function removeSourcePage(sourcePage: SourceOfficeNotePage) {
    setRemovedSourcePages([...removedSourcePages, sourcePage]);
  }

  function isAlreadyAdded(pageId: string) {
    return sourcePages.some(p => p.pageId === pageId);
  }

  async function updateBotContent() {
    setInProgressUpdate(true);
    await Fire.bot.deleteSources(removedSourcePages);
    await Fire.bot.updateSoribotContent({ soribotId: bot.id, type: 'officenote-page' });
    setAlert({ open: true, title: t`Done!`, message: t`The knowledge content is updated. The result can be viewed in the Knowledge viewer.`, confirm: null });
    setRemovedSourcePages([]);
    reloadSourcePages();
    setInProgressUpdate(false);
  }
}
